// 档2 — OpenAI Realtime playground（GA，text/JSON 事件协议）。产品内调用体验,与 ws-playground-asr
// 平行,但驱动 Realtime 的 JSON 事件流而非火山二进制帧。
//
// Flow: getWsToken → 连 ws-gateway（桥到 wss://api.openai.com/v1/realtime）→ session.created →
// 文本:conversation.item.create + response.create → 流式 response.output_text.delta 进气泡;
// 语音:麦克风 PCM16/24k → input_audio_buffer.append(server VAD 自动回话)+ 播放 output_audio.delta。
// response.done 带 token usage。纯前端估算成本(真实计费在网关侧)。

import React from 'react';
import { XPrim, XIcons } from '../../primitives.jsx';
import { XApi } from '../../services.js';
import WsRealtimeOrb from './ws-realtime-orb.jsx';

const RATE = 24000; // Realtime PCM16 mono（输入&输出）
const FRAME_MS = 120;

// 沉浸式语音模式的相位文案(球下方状态提示)。
const PHASE_LABEL = {
  idle: '未连接',
  connecting: '连接中…',
  ready: '准备就绪',
  microphone: '正在开启麦克风…',
  listening: '聆听中…',
  thinking: '思考中…',
  speaking: '说话中…',
};

// ── 小工具 ──────────────────────────────────────────────────────────────
const bytesToBase64 = (bytes) => {
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  return btoa(bin);
};
const base64ToBytes = (b64) => {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};
// Float32 @inputRate → Int16 @RATE（线性抽取）
function floatToPcm16(float32, inputRate) {
  const ratio = inputRate / RATE;
  const outLen = Math.floor(float32.length / ratio);
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const s = Math.max(-1, Math.min(1, float32[Math.floor(i * ratio)]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return new Uint8Array(out.buffer);
}
const isSecure = () =>
  typeof window !== 'undefined' &&
  (window.isSecureContext || /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname));

// 默认按端点 wsBilling 的模态单价做客户端成本估算（真实计费在网关）。
const priceOf = (wsBilling) => ({
  inText: Number(wsBilling.inputTextTokenUsd ?? wsBilling.inputTokenUsd) || 0,
  inAudio: Number(wsBilling.inputAudioTokenUsd ?? wsBilling.inputTokenUsd) || 0,
  outText: Number(wsBilling.outputTextTokenUsd ?? wsBilling.outputTokenUsd) || 0,
  outAudio: Number(wsBilling.outputAudioTokenUsd ?? wsBilling.outputTokenUsd) || 0,
});

export default function WsPlaygroundRealtime({ api, endpoint }) {
  const wsConfig = (endpoint && endpoint._raw && endpoint._raw.wsConfig) || {};
  const wsBilling = (endpoint && endpoint._raw && endpoint._raw.wsBilling) || {};
  const clientPath = wsConfig.clientPath || '/v1/realtime';
  const price = React.useMemo(() => priceOf(wsBilling), [wsBilling]);

  const [status, setStatus] = React.useState('idle'); // idle|connecting|ready|recording|closed|error
  const [turns, setTurns] = React.useState([]);
  const [input, setInput] = React.useState('');
  const [usage, setUsage] = React.useState({ inTok: 0, outTok: 0, inText: 0, inAudio: 0, outText: 0, outAudio: 0 });
  const [errorMsg, setErrorMsg] = React.useState('');
  const [modality, setModality] = React.useState('audio'); // text|audio（默认语音，直接进沉浸式云球）
  const [voice, setVoice] = React.useState('alloy'); // 语音输出音色
  const [instructions, setInstructions] = React.useState(''); // 系统指令
  const [transcription, setTranscription] = React.useState('whisper-1'); // 输入语音转写模型（''=关闭）
  const [showCfg, setShowCfg] = React.useState(true); // 默认展开，让系统指令一进来就可见
  const [phase, setPhase] = React.useState('idle'); // 云球相位:idle|connecting|ready|listening|thinking|speaking

  const wsRef = React.useRef(null);
  const statusRef = React.useRef('idle');
  const asstRef = React.useRef(null); // 进行中的 assistant turn id
  const idRef = React.useRef(0);
  const modalityRef = React.useRef('audio');
  const voiceRef = React.useRef('alloy');
  const instructionsRef = React.useRef('');
  const transcriptionRef = React.useRef('whisper-1');
  const userItemsRef = React.useRef({}); // item_id → 用户转写 turn id
  // 音频
  const micCtxRef = React.useRef(null);
  const micStreamRef = React.useRef(null);
  const micNodeRef = React.useRef(null);
  const micRequestRef = React.useRef(null);
  const startMicRef = React.useRef(null);
  const playCtxRef = React.useRef(null);
  const playAtRef = React.useRef(0);
  const playSourcesRef = React.useRef([]); // 已排期的音频源节点，打断时全停
  const responseActiveRef = React.useRef(false); // 是否有 response 在生成（打断/取消判定）
  // 语音可视化:输入/输出各挂一个 AnalyserNode,喂给云球读电平。
  const micAnalyserRef = React.useRef(null);
  const outAnalyserRef = React.useRef(null);

  const setStatusBoth = (s) => { statusRef.current = s; setStatus(s); };
  const nextId = () => `t${idRef.current++}`;

  const stopMicrophone = React.useCallback(() => {
    micRequestRef.current = null;
    if (micNodeRef.current) { try { micNodeRef.current.disconnect(); } catch {} micNodeRef.current = null; }
    if (micCtxRef.current) { try { micCtxRef.current.close(); } catch {} micCtxRef.current = null; }
    if (micStreamRef.current) { try { micStreamRef.current.getTracks().forEach((t) => t.stop()); } catch {} micStreamRef.current = null; }
    micAnalyserRef.current = null;
  }, []);

  const teardownAudio = React.useCallback(() => {
    stopMicrophone();
    if (playCtxRef.current) { try { playCtxRef.current.close(); } catch {} playCtxRef.current = null; }
    outAnalyserRef.current = null;
    playAtRef.current = 0;
    playSourcesRef.current = [];
  }, [stopMicrophone]);

  const teardown = React.useCallback((closeSocket = true) => {
    teardownAudio();
    if (closeSocket && wsRef.current) { try { wsRef.current.close(1000, 'client stop'); } catch {} wsRef.current = null; }
  }, [teardownAudio]);

  React.useEffect(() => () => teardown(true), [teardown]);

  const send = React.useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
  }, []);

  const ensurePlaybackContext = React.useCallback(() => {
    if (!playCtxRef.current) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) throw new Error('浏览器不支持 AudioContext');
      const context = new AudioCtx();
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      analyser.connect(context.destination);
      playCtxRef.current = context;
      outAnalyserRef.current = analyser;
      playAtRef.current = context.currentTime;
    }
    const context = playCtxRef.current;
    if (context.state === 'suspended') void context.resume();
    return context;
  }, []);

  // 播放一段 base64 PCM16 24k（无缝排队）
  const playAudio = React.useCallback((b64) => {
    try {
      const ctx = ensurePlaybackContext();
      const outBus = outAnalyserRef.current || ctx.destination;
      const bytes = base64ToBytes(b64);
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      const n = Math.floor(bytes.byteLength / 2);
      const buf = ctx.createBuffer(1, n, RATE);
      const ch = buf.getChannelData(0);
      for (let i = 0; i < n; i++) ch[i] = view.getInt16(i * 2, true) / 0x8000;
      const src = ctx.createBufferSource();
      src.buffer = buf; src.connect(outBus);
      src.onended = () => { playSourcesRef.current = playSourcesRef.current.filter((s) => s !== src); };
      playSourcesRef.current.push(src);
      const at = Math.max(playAtRef.current, ctx.currentTime);
      src.start(at); playAtRef.current = at + buf.duration;
    } catch { /* 忽略坏帧 */ }
  }, [ensurePlaybackContext]);

  // 打断:立即停掉所有已排期的助手语音（保留 playCtx 供下一句续播），不动麦克风。
  const stopPlayback = React.useCallback(() => {
    for (const s of playSourcesRef.current) { try { s.stop(); } catch {} }
    playSourcesRef.current = [];
    if (playCtxRef.current) playAtRef.current = playCtxRef.current.currentTime;
  }, []);

  const appendAssistant = React.useCallback((delta) => {
    if (!delta) return;
    setTurns((prev) => {
      let id = asstRef.current;
      if (!id) { id = nextId(); asstRef.current = id; return [...prev, { id, role: 'assistant', text: delta, streaming: true }]; }
      return prev.map((t) => (t.id === id ? { ...t, text: t.text + delta } : t));
    });
  }, []);

  const handleEvent = React.useCallback((e) => {
    const ty = (e && e.type) || '';
    if (ty === 'session.created') {
      // 下发会话配置:输出模态 + 系统指令 + 输入音频(server VAD + 可选转写) + 语音输出音色。
      const session = { type: 'realtime', output_modalities: [modalityRef.current] };
      if (instructionsRef.current) session.instructions = instructionsRef.current;
      session.audio = {
        input: {
          format: { type: 'audio/pcm', rate: RATE },
          turn_detection: { type: 'server_vad' },
          // 开启输入语音转写 → 服务端回 input_audio_transcription 事件,才能看到用户说了什么。
          ...(transcriptionRef.current ? { transcription: { model: transcriptionRef.current } } : {}),
        },
        output: { format: { type: 'audio/pcm', rate: RATE }, voice: voiceRef.current || 'alloy' },
      };
      send({ type: 'session.update', session });
      return;
    }
    if (ty === 'session.updated') {
      setStatusBoth('ready');
      void startMicRef.current?.();
      return;
    }
    // 语音输入提交(server VAD 结束一轮)→ 立即占位创建用户气泡,保证它排在助手回复之前。
    // (转写事件是异步的、常晚于 response 流到达;若等转写才建气泡,顺序会颠倒。)
    if (ty === 'input_audio_buffer.committed') {
      const itemId = e.item_id;
      if (itemId && transcriptionRef.current && !userItemsRef.current[itemId]) {
        const id = nextId(); userItemsRef.current[itemId] = id;
        setTurns((prev) => [...prev, { id, role: 'user', text: '', streaming: true }]);
      }
      return;
    }
    // 用户语音转写(需开启 transcription)→ 填入上面占位的用户气泡。
    if (ty === 'conversation.item.input_audio_transcription.delta') {
      const itemId = e.item_id, d = e.delta || '';
      setTurns((prev) => {
        let id = userItemsRef.current[itemId];
        if (!id) { id = nextId(); userItemsRef.current[itemId] = id; return [...prev, { id, role: 'user', text: d, streaming: true }]; }
        return prev.map((t) => (t.id === id ? { ...t, text: t.text + d } : t));
      });
      return;
    }
    if (ty === 'conversation.item.input_audio_transcription.completed') {
      const itemId = e.item_id, full = e.transcript || '';
      setTurns((prev) => {
        let id = userItemsRef.current[itemId];
        if (!id) { id = nextId(); userItemsRef.current[itemId] = id; return [...prev, { id, role: 'user', text: full, streaming: false }]; }
        return prev.map((t) => (t.id === id ? { ...t, text: full || t.text, streaming: false } : t));
      });
      return;
    }
    if (ty === 'error') { const m = (e.error && e.error.message) || JSON.stringify(e.error || e); setErrorMsg(m); return; }
    // 用户开始说话（server VAD）→ 打断:立即停掉助手上一句已排期的语音(否则会播完才答新问题)。
    if (ty === 'input_audio_buffer.speech_started') { stopPlayback(); setPhase('listening'); return; }
    if (ty === 'response.created') { responseActiveRef.current = true; setPhase('thinking'); return; }
    if (ty.endsWith('output_text.delta') || ty === 'response.text.delta') { appendAssistant(e.delta || ''); return; }
    if (ty.endsWith('audio_transcript.delta')) { appendAssistant(e.delta || ''); return; }
    if (ty.endsWith('output_audio.delta') || ty === 'response.audio.delta') { if (e.delta) { playAudio(e.delta); setPhase('speaking'); } return; }
    if (ty === 'response.done') {
      responseActiveRef.current = false; setPhase('ready');
      const id = asstRef.current; asstRef.current = null;
      if (id) setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, streaming: false } : t)));
      const u = e.response && e.response.usage;
      if (u) {
        const it = u.input_token_details || {}, ot = u.output_token_details || {};
        setUsage((p) => ({
          inTok: p.inTok + (u.input_tokens || 0), outTok: p.outTok + (u.output_tokens || 0),
          inText: p.inText + (it.text_tokens || 0), inAudio: p.inAudio + (it.audio_tokens || 0),
          outText: p.outText + (ot.text_tokens || 0), outAudio: p.outAudio + (ot.audio_tokens || 0),
        }));
      }
    }
  }, [appendAssistant, playAudio, send, stopPlayback]);

  const connect = async () => {
    try {
      ensurePlaybackContext();
    } catch (e) {
      setErrorMsg('浏览器无法初始化音频播放: ' + (e && e.message ? e.message : e));
      return;
    }
    setErrorMsg(''); setTurns([]); setUsage({ inTok: 0, outTok: 0, inText: 0, inAudio: 0, outText: 0, outAudio: 0 });
    asstRef.current = null; modalityRef.current = modality;
    voiceRef.current = voice; instructionsRef.current = instructions; transcriptionRef.current = transcription;
    responseActiveRef.current = false; playSourcesRef.current = []; userItemsRef.current = {};
    setStatusBoth('connecting'); setPhase('connecting');
    let token;
    try {
      const minted = await XApi.keyService.getWsToken(endpoint._raw?.id || endpoint.id);
      token = minted && minted.token;
      if (!token) throw new Error('mint returned no token');
    } catch (e) { teardownAudio(); setStatusBoth('error'); setErrorMsg('ws-token mint 失败: ' + (e && e.message ? e.message : e)); return; }

    const url = XApi.helpers.buildWsGatewayUrl(api.host, clientPath, { endpoint: endpoint._raw?.id || endpoint.id });
    let ws;
    try { ws = new WebSocket(url, XApi.helpers.buildWsCredentialProtocols(token)); } catch (e) { teardownAudio(); setStatusBoth('error'); setErrorMsg('WebSocket 构造失败: ' + (e && e.message ? e.message : e)); return; }
    wsRef.current = ws;
    ws.onmessage = (ev) => { if (typeof ev.data !== 'string') return; try { handleEvent(JSON.parse(ev.data)); } catch {} };
    ws.onerror = () => setErrorMsg((m) => m || 'WebSocket error');
    ws.onclose = (ev) => {
      teardown(false); wsRef.current = null; setPhase('idle');
      if (statusRef.current !== 'error') setStatusBoth('closed');
      if (ev.code !== 1000 && ev.code !== 1005) setErrorMsg((m) => m || `Closed · code ${ev.code}${ev.reason ? ` · ${ev.reason}` : ''}`);
    };
  };

  const disconnect = () => { teardown(true); setStatusBoth('closed'); setPhase('idle'); };

  const sendText = () => {
    const v = input.trim();
    if (!v || (statusRef.current !== 'ready' && statusRef.current !== 'recording')) return;
    // 文本打断:若有 response 在生成,先 cancel + 停播,再发新问题。
    if (responseActiveRef.current) { send({ type: 'response.cancel' }); stopPlayback(); responseActiveRef.current = false; }
    setTurns((prev) => [...prev, { id: nextId(), role: 'user', text: v }]);
    send({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: v }] } });
    send({ type: 'response.create', response: { output_modalities: [modalityRef.current] } });
    setInput('');
  };

  const startMic = async () => {
    if (statusRef.current !== 'ready' || micStreamRef.current || micRequestRef.current) return;
    setPhase('microphone');
    if (!isSecure()) { setErrorMsg('麦克风需要 HTTPS 或 localhost'); setPhase('ready'); return; }
    if (!navigator.mediaDevices?.getUserMedia) { setErrorMsg('当前浏览器不支持麦克风采集'); setPhase('ready'); return; }
    const socket = wsRef.current;
    const request = {};
    micRequestRef.current = request;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
      if (micRequestRef.current !== request || wsRef.current !== socket || socket?.readyState !== WebSocket.OPEN || statusRef.current !== 'ready') {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) throw new Error('浏览器不支持 AudioContext');
      const ctx = new AudioCtx(); micCtxRef.current = ctx;
      if (ctx.state === 'suspended') await ctx.resume();
      if (micRequestRef.current !== request || wsRef.current !== socket || socket?.readyState !== WebSocket.OPEN || statusRef.current !== 'ready') {
        try { ctx.close(); } catch {}
        if (micCtxRef.current === ctx) micCtxRef.current = null;
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      micStreamRef.current = stream;
      const source = ctx.createMediaStreamSource(stream);
      // 并联一个 analyser 读输入电平(不影响采集链路),喂给云球。
      const inAn = ctx.createAnalyser(); inAn.fftSize = 1024;
      source.connect(inAn); micAnalyserRef.current = inAn;
      const proc = ctx.createScriptProcessor(4096, 1, 1); micNodeRef.current = proc;
      proc.onaudioprocess = (e) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        const pcm = floatToPcm16(e.inputBuffer.getChannelData(0), ctx.sampleRate);
        if (pcm.length) send({ type: 'input_audio_buffer.append', audio: bytesToBase64(pcm) });
      };
      source.connect(proc); proc.connect(ctx.destination);
      setStatusBoth('recording');
      setPhase('listening');
    } catch (e) {
      if (stream && micStreamRef.current !== stream) { try { stream.getTracks().forEach((track) => track.stop()); } catch {} }
      if (micRequestRef.current === request && wsRef.current === socket) {
        stopMicrophone();
        setStatusBoth('ready');
        setPhase('ready');
        setErrorMsg('无法访问麦克风: ' + (e && e.message ? e.message : e));
      }
    } finally {
      if (micRequestRef.current === request) micRequestRef.current = null;
    }
  };
  React.useEffect(() => { startMicRef.current = startMic; });

  const live = status === 'ready' || status === 'recording' || status === 'connecting';
  const ready = status === 'ready' || status === 'recording';
  // 沉浸式语音模式:只显示最近一轮 user/assistant 作为字幕。
  const lastUser = React.useMemo(() => [...turns].reverse().find((t) => t.role === 'user'), [turns]);
  const lastAsst = React.useMemo(() => [...turns].reverse().find((t) => t.role === 'assistant'), [turns]);
  const estCost = usage.inText * price.inText + usage.inAudio * price.inAudio + usage.outText * price.outText + usage.outAudio * price.outAudio;
  const dot = status === 'recording' ? 'var(--err)' : status === 'ready' ? 'var(--ok, #35c98a)' : status === 'connecting' ? 'var(--c-amber, #f0b23a)' : status === 'error' ? 'var(--err)' : 'var(--fg-4)';

  return (
    <div className="col gap-3" style={{ minWidth: 0 }}>
      {/* 连接 + 状态 + 用量 */}
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">Realtime 对话 · OpenAI Realtime</div>
          <div className="row gap-2" style={{ alignItems: 'center', fontSize: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
            <span style={{ color: 'var(--fg-3)' }}>{status}</span>
          </div>
        </div>
        <div style={{ padding: 14 }}>
          <div className="row gap-3" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {!live ? (
              <button className="btn btn-primary" onClick={connect} disabled={status === 'connecting'} style={{ height: 40, minWidth: 130 }}>
                {status === 'connecting' ? <><div className="spinner" style={{ width: 14, height: 14 }} /> 连接中…</> : <><XIcons.IconPlay size={14} /> 连接</>}
              </button>
            ) : (
              <button className="btn" onClick={disconnect} style={{ height: 40, minWidth: 130, borderColor: 'var(--err)', color: 'var(--err)' }}>
                <XIcons.IconPause size={14} /> 断开
              </button>
            )}
            <label className="row gap-2" style={{ alignItems: 'center', fontSize: 12.5, color: 'var(--fg-3)', whiteSpace: 'nowrap', flexShrink: 0 }}>
              <span style={{ whiteSpace: 'nowrap' }}>输出</span>
              <select className="input" value={modality} disabled={live} onChange={(e) => setModality(e.target.value)} style={{ height: 32, maxWidth: 120 }}>
                <option value="text">文本</option>
                <option value="audio">语音</option>
              </select>
            </label>
          </div>
          <div className="row gap-3" style={{ marginTop: 12, fontSize: 12.5, color: 'var(--fg-3)', flexWrap: 'wrap' }}>
            <span>输入 token <span className="mono" style={{ color: 'var(--fg-1)' }}>{usage.inTok}</span></span>
            <span>输出 token <span className="mono" style={{ color: 'var(--fg-1)' }}>{usage.outTok}</span> <span style={{ color: 'var(--fg-4)' }}>({usage.outAudio} 音频)</span></span>
            {estCost > 0 && <span>估算 <span className="mono" style={{ color: 'var(--fg-1)' }}>${estCost.toFixed(6)}</span></span>}
          </div>
        </div>
      </div>

      {/* 会话配置 */}
      <div className="card">
        <button onClick={() => setShowCfg((v) => !v)}
          style={{ width: '100%', padding: '10px 14px', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: showCfg ? '1px solid var(--line)' : 'none', color: 'var(--fg-2)' }}>
          <span className="micro">会话配置 · 系统指令 / 音色 / 语音转写</span>
          <XIcons.IconChevronDown size={13} style={{ transform: showCfg ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>
        {showCfg && (
          <div className="col gap-3" style={{ padding: 14 }}>
            <label className="col gap-1">
              <span style={{ fontSize: 12, color: 'var(--fg-2)', fontWeight: 600 }}>系统指令 instructions（System Prompt）</span>
              <textarea className="input" rows={3} style={{ resize: 'vertical' }} value={instructions} disabled={live}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="给模型设定人设 / 语气 / 任务，例如：You are a friendly assistant who answers concisely in Chinese." />
              <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>连接前设置，会随 session.update 下发给模型（可选，留空则用默认行为）。</span>
            </label>
            <label className="col gap-1">
              <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>音色 voice（语音输出）</span>
              <select className="input" value={voice} disabled={live} onChange={(e) => setVoice(e.target.value)} style={{ maxWidth: 220 }}>
                {['alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse', 'marin', 'cedar'].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="col gap-1">
              <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>输入语音转写（开启才能看到你说了什么）</span>
              <select className="input" value={transcription} disabled={live} onChange={(e) => setTranscription(e.target.value)} style={{ maxWidth: 260 }}>
                <option value="">关闭</option>
                <option value="whisper-1">whisper-1</option>
                <option value="gpt-4o-mini-transcribe">gpt-4o-mini-transcribe</option>
                <option value="gpt-4o-transcribe">gpt-4o-transcribe</option>
              </select>
            </label>
            {live && <div style={{ fontSize: 11, color: 'var(--fg-4)' }}>会话进行中,配置锁定;断开后可改。</div>}
          </div>
        )}
      </div>

      {errorMsg && (
        <div className="row gap-2" style={{ padding: '10px 12px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: 8, fontSize: 12.5, color: 'var(--err)' }}>
          <XIcons.IconInfo size={13} /> <span style={{ overflowWrap: 'anywhere' }}>{errorMsg}</span>
        </div>
      )}

      {/* 对话 */}
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">对话</div>
          {turns.length > 0 && <XPrim.CopyBtn text={turns.map((t) => `${t.role}: ${t.text}`).join('\n')} />}
        </div>
        {modality === 'audio' ? (
          /* 沉浸式语音模式:云球 + 相位提示 + 最近一轮字幕 */
          <div className="col" style={{ alignItems: 'center', justifyContent: 'center', gap: 16, padding: '30px 16px', minHeight: 340,
            background: 'radial-gradient(120% 80% at 50% 32%, rgba(80,120,220,0.06), transparent)' }}>
            <WsRealtimeOrb micAnalyserRef={micAnalyserRef} outAnalyserRef={outAnalyserRef} phase={live ? phase : 'idle'} size={200} />
            <div className="micro" style={{ color: 'var(--fg-3)', letterSpacing: 0.3 }}>{PHASE_LABEL[live ? phase : 'idle']}</div>
            <div className="col gap-1" style={{ alignItems: 'center', textAlign: 'center', maxWidth: 480, minHeight: 42 }}>
              {lastUser && <div style={{ fontSize: 12.5, color: 'var(--fg-4)', overflowWrap: 'anywhere' }}>{lastUser.text || '…'}</div>}
              {lastAsst && (
                <div style={{ fontSize: 15, color: 'var(--fg-1)', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                  {lastAsst.text}{lastAsst.streaming && <span className="mono" style={{ color: 'var(--fg-3)' }}>▋</span>}
                </div>
              )}
              {!lastUser && !lastAsst && (
                <div style={{ fontSize: 13, color: 'var(--fg-4)' }}>
                  {status === 'recording'
                    ? '麦克风已自动开启，可直接说话。'
                    : ready
                      ? '麦克风未开启，仍可在下方发送文字。'
                      : '点「连接」开始 Realtime 对话。'}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="col gap-2" style={{ padding: 14, minHeight: 160, maxHeight: 420, overflowY: 'auto' }}>
            {turns.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--fg-4)' }}>
                {ready ? '已连接。下方输入消息,或切到「语音」用麦克风对话。' : '点「连接」开始 Realtime 对话。'}
              </div>
            ) : turns.map((t) => (
              <div key={t.id} className="row" style={{ justifyContent: t.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth: '82%', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', padding: '8px 12px', borderRadius: 10,
                  background: t.role === 'user' ? 'var(--accent, #5b8cff)' : 'var(--bg-3, var(--panel-2))',
                  color: t.role === 'user' ? '#fff' : 'var(--fg-1)', border: t.role === 'user' ? 'none' : '1px solid var(--line)' }}>
                  {t.text || (t.streaming ? '' : <span style={{ color: 'var(--fg-4)' }}>(无文本)</span>)}
                  {t.streaming && <span className="mono" style={{ color: 'var(--fg-3)' }}>▋</span>}
                </div>
              </div>
            ))}
          </div>
        )}
        {/* 输入区 */}
        <div className="row gap-2" style={{ padding: 12, borderTop: '1px solid var(--line)', alignItems: 'flex-end' }}>
          <textarea className="input mono" rows={2} style={{ resize: 'none', flex: 1 }}
            placeholder={ready ? '输入消息…(Enter 发送)' : '先连接…'} value={input} disabled={!ready}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); } }} />
          <button className="btn btn-primary" style={{ minWidth: 78 }} disabled={!ready || !input.trim()} onClick={sendText}>发送</button>
        </div>
      </div>
    </div>
  );
}
